import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const CAT = {
  coach_cert_insurance: "Coach certification / insurance expiry",
  player_feedback: "Player feedback",
  initial_assessment: "Initial assessment result",
};
const WINDOWS = [48, 72, 120, 168];

// Admin: review blocked emails and resend selected ones to their real recipients.
export function BlockedEmailsResend() {
  const [hours, setHours] = useState(48);
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const load = (h = hours) => api.get("/admin/blocked-emails", { params: { hours: h } }).then((r) => {
    setData(r.data);
    setPicked(Object.fromEntries(r.data.items.filter((i) => i.recipient).map((i) => [i.id, true])));
  }).catch(() => setData({ items: [] }));
  useEffect(() => { load(hours); }, [hours]); // eslint-disable-line react-hooks/exhaustive-deps

  const ids = Object.keys(picked).filter((k) => picked[k]);
  const resend = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/blocked-emails/resend", { ids });
      setResults(r.results);
      const notSent = r.results.length - r.sent;
      if (r.sent) toast.success(`${r.sent} email${r.sent === 1 ? "" : "s"} sent`);
      if (notSent) toast.warning(`${notSent} not delivered — check the app is in production mode and email is switched on`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Resend failed"); }
    finally { setBusy(false); }
  };

  const items = data?.items || [];
  return (
    <section className="card-surface p-6 space-y-4" data-testid="blocked-emails-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-mono text-[#CCFF00]">BLOCKED EMAILS</div>
          <h2 className="text-base md:text-lg font-bold mt-1">Review and resend</h2>
          <p className="text-xs text-[#A3A3A3] mt-1 max-w-xl">Certification and insurance expiry, player feedback and initial assessment emails that were blocked, or delivered to the preview redirect address instead of the real person. Ticked emails are resent as originally written to the intended recipient.</p>
        </div>
        <div className="flex gap-1" data-testid="blocked-emails-window">
          {WINDOWS.map((h) => (
            <button key={h} type="button" onClick={() => setHours(h)} data-testid={`blocked-emails-window-${h}`}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold border ${hours === h ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-[#404040] text-[#A3A3A3] hover:text-white"}`}>
              {h === 168 ? "7 days" : `${h}h`}
            </button>
          ))}
        </div>
      </div>
      {data?.environment && data.environment !== "production" && (
        <div className="text-xs p-3 border border-[#FF9F1C]/40 bg-[#FF9F1C]/5" data-testid="blocked-emails-env-warning">
          This copy of the app is not in production mode, so resent emails will be blocked or redirected again.
        </div>
      )}
      {!data ? <Loader2 className="w-4 h-4 animate-spin" /> : !items.length ? (
        <div className="text-sm text-[#A3A3A3]" data-testid="blocked-emails-empty">No blocked emails in this window.</div>
      ) : (
        <ul className="divide-y divide-[#1F1F1F]" data-testid="blocked-emails-list">
          {items.map((it) => (
            <li key={it.id} className="py-3 flex items-start gap-3" data-testid={`blocked-email-${it.id}`}>
              <input type="checkbox" className="mt-1 accent-[#CCFF00]" checked={!!picked[it.id]} disabled={!it.recipient}
                onChange={(e) => setPicked((p) => ({ ...p, [it.id]: e.target.checked }))} data-testid={`blocked-email-check-${it.id}`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{it.subject}</div>
                <div className="text-xs text-[#A3A3A3] truncate">
                  To {it.to_name ? `${it.to_name} · ` : ""}{it.recipient || "unknown recipient"} · {new Date(it.created_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </div>
                <span className="inline-block mt-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border border-[#404040] text-[#A3A3A3]">{CAT[it.category]}</span>
                <span className="inline-block mt-1.5 ml-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border border-[#FF9F1C]/60 text-[#FF9F1C]" data-testid={`blocked-email-state-${it.id}`}>
                  {it.misdelivered_to ? `Delivered to ${it.misdelivered_to}` : "Blocked"}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {results && (
        <div className="border border-[#262626] p-3 space-y-1" data-testid="blocked-emails-results">
          <div className="label-mono text-[#CCFF00]">LAST RESEND · {results.filter((r) => r.status === "sent").length} of {results.length} delivered</div>
          {results.map((r) => (
            <div key={r.id} className="text-xs flex gap-2" data-testid={`blocked-email-result-${r.id}`}>
              <span className={`font-bold uppercase w-24 shrink-0 ${r.status === "sent" ? "text-[#CCFF00]" : "text-[#FF4500]"}`}>{r.status === "sent" ? "Sent" : (r.status || "not sent")}</span>
              <span className="truncate text-[#E5E5E5]">{r.to_name ? `${r.to_name} · ` : ""}{r.to || "no recipient"}{r.subject ? ` — ${r.subject}` : ""}</span>
            </div>
          ))}
        </div>
      )}
      {!!items.length && (
        <div className="flex justify-end">
          <button type="button" onClick={resend} disabled={busy || !ids.length} data-testid="blocked-emails-resend"
            className="min-h-[40px] px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black inline-flex items-center gap-2 disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Resend {ids.length} selected
          </button>
        </div>
      )}
    </section>
  );
}
